import React, { useState, useEffect } from 'react';
import InteractiveMap from '../components/InteractiveMap';
import { apiConfig, buildApiUrl } from '../config/api';

interface CityData {
  city: string;
  state: string;
  memberCount: number;
  primaryPercentage: number;
  zipCount: number;
  coordinates?: { lat: number; lng: number } | null;
}

interface GeographicData {
  cityDistribution: CityData[];
  summary: {
    totalCities: number;
    topCity: string | null;
    topCityCount: number;
  };
}

const GeographicDashboard: React.FC = () => {
  const [data, setData] = useState<GeographicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGeographicData();
  }, []);

  const fetchGeographicData = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(apiConfig.endpoints.analytics.geographic));
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError('Failed to fetch geographic data');
      }
    } catch (err) {
      setError('Error fetching geographic data');
      console.error('Geographic data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={fetchGeographicData}
          className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return <div>No geographic data available</div>;
  }

  const getHeatMapColor = (memberCount: number, maxCount: number) => {
    const intensity = memberCount / maxCount;
    if (intensity > 0.8) return 'bg-red-600 text-white';
    if (intensity > 0.6) return 'bg-red-500 text-white';
    if (intensity > 0.4) return 'bg-orange-500 text-white';
    if (intensity > 0.2) return 'bg-yellow-500 text-gray-900';
    return 'bg-green-200 text-gray-900';
  };

  const maxCityCount = Math.max(...data.cityDistribution.map(c => c.memberCount));

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">📍 Geographic Distribution</h1>
        <p className="text-gray-600 mb-6">
          Member distribution across cities and states to identify areas for potential small group expansion.
        </p>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800">Total Cities</h3>
            <p className="text-2xl font-bold text-blue-900">{data.summary.totalCities}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-purple-800">Top City</h3>
            <p className="text-lg font-bold text-purple-900">{data.summary.topCity}</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-orange-800">Top City Members</h3>
            <p className="text-2xl font-bold text-orange-900">{data.summary.topCityCount}</p>
          </div>
        </div>

        {/* Heat Map Style City Grid */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🗺️ Member Heat Map by City</h2>
          <p className="text-sm text-gray-600 mb-4">
            Larger concentrations indicate potential areas for new small groups
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {data.cityDistribution.slice(0, 20).map((city) => (
              <div
                key={`${city.city}-${city.state}`}
                className={`p-3 rounded-lg border-2 transition-all hover:scale-105 cursor-pointer ${getHeatMapColor(city.memberCount, maxCityCount)}`}
                title={`${city.memberCount} members in ${city.city.charAt(0).toUpperCase() + city.city.slice(1).toLowerCase()}, ${city.state}`}
              >
                <div className="text-sm font-semibold">
                  {city.city.charAt(0).toUpperCase() + city.city.slice(1).toLowerCase()}, {city.state}
                </div>
                <div className="text-xs opacity-90">
                  {city.zipCount} ZIP codes • {city.memberCount} people
                </div>
                <div className="text-xs opacity-75">
                  {city.primaryPercentage}% primary addresses
                </div>
              </div>
            ))}
          </div>

          {/* Heat Map Legend */}
          <div className="mt-4 flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Member Count:</span>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-200 rounded"></div>
              <span className="text-xs">Low</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-yellow-500 rounded"></div>
              <span className="text-xs">Medium</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-orange-500 rounded"></div>
              <span className="text-xs">High</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-600 rounded"></div>
              <span className="text-xs">Very High</span>
            </div>
          </div>
        </div>

        {/* Visual Map Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">🗺️ Interactive Map - East Texas Region</h2>
          <p className="text-sm text-gray-600 mb-4">
            Click on circles to see detailed information about each city. Zoom and pan to explore the region.
          </p>
          
          <InteractiveMap cities={data.cityDistribution} />
        </div>

        {/* Insights and Recommendations */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-3">💡 Small Group Expansion Insights</h2>
          <div className="space-y-2 text-sm text-blue-800">
            {data.summary.topCity && (
              <p>
                • <strong>Winnsboro Focus:</strong> With {data.summary.topCityCount} members, consider establishing 3-4 new groups
              </p>
            )}
            {data.cityDistribution.length >= 2 && (
              <p>
                • <strong>Regional Expansion:</strong> {data.cityDistribution[1]?.city} ({data.cityDistribution[1]?.memberCount} members) and {data.cityDistribution[2]?.city} ({data.cityDistribution[2]?.memberCount} members) are ready for new groups
              </p>
            )}
            <p>
              • <strong>Geographic Strategy:</strong> Use the heat map to identify optimal meeting locations
            </p>
            <p>
              • <strong>High-concentration areas</strong> may benefit from multiple groups to maintain intimacy
            </p>
            <p>
              • <strong>Areas with lower primary address percentages</strong> may indicate transient populations
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeographicDashboard;
